import React, { DOMAttributes, useEffect, useMemo, useState } from 'react';

import {
  ActionList,
  Badge,
  color,
  Icon,
  Input,
  Typography,
  Popover,
  Avatar,
  Button,
  spacing,
  toast
} from '@uniquegood/realworld-studio-design';

import { css } from '@emotion/react';
import useLocalStorage from 'react-use-localstorage';

import { Link, useHistory } from 'react-router-dom';
import { breakpoint, onMobile } from '@/styles/responsive';

import { KeyboardFocusStyle, RowFlex } from '@/styles';
import LoadingSpinner from '@/components/LoadingSpinner';
import {
  useIsAdminSelector,
  useParamsWithMatchPath,
  useRequest,
  useRequestImmutable
} from '@/hooks';

import { levelToKorean } from '@/const';
import { CssStyle } from '@/utils';
import { logout } from '@/api';
import { channelsMapper } from '@/utils/channelsMapper';
import { isSchoolAdminAccessiblePlan, isSchoolAdminRole } from '@/utils/roleAccess';

interface UserMenuProps extends CssStyle {
  onClose?(): void;
}

type ChannelMenuItem = Omit<ApplicationResponseModel, 'members'> & {
  level?: MemberLevel | null;
};

function mapChannelsByRole(apps: ApplicationResponseModel[], me: MyInfo): ChannelMenuItem[] {
  const membershipChannels = channelsMapper(apps, me.id) as ChannelMenuItem[];

  const membershipIds = new Set(membershipChannels.map((channel) => channel.id));
  const accessibleChannels: ChannelMenuItem[] = apps
    .filter((app) => isSchoolAdminAccessiblePlan(app.planType))
    .filter((app) => !membershipIds.has(app.id))
    .map((app) => {
      const { members, ...forwardedProps } = app;
      return { ...forwardedProps, level: null };
    });

  return [...membershipChannels, ...accessibleChannels];
}

function hasSchoolAdminAccessibleChannels(apps: ApplicationResponseModel[], me: MyInfo) {
  const membershipIds = new Set((channelsMapper(apps, me.id) as ChannelMenuItem[]).map((c) => c.id));

  return apps.some(
    (app) => isSchoolAdminAccessiblePlan(app.planType) && !membershipIds.has(app.id)
  );
}

export default function UserMenu({ cssStyle, onClose }: UserMenuProps) {
  const { data: me } = useRequestImmutable<MyInfo>('/api/me');
  const history = useHistory();
  const { data: { data: apps } = {} } = useRequestImmutable<
    CommonResponseModel<ApplicationResponseModel[]>
  >('/apps', undefined, {
    headers: {
      'x-rwd-api-version': '1.1'
    }
  });

  const channels = me && apps ? mapChannelsByRole(apps, me) : undefined;
  const { data: isAdmin } = useIsAdminSelector();
  const isSchoolAdmin = Boolean(
    me &&
      apps &&
      (isSchoolAdminRole(me.role) || hasSchoolAdminAccessibleChannels(apps, me))
  );

  const email =
    me?.credentials?.[0].provider === 'Self'
      ? me.credentials[0].providerId
      : me?.credentials?.[0].provider;

  const avatarContent = me ? (
    <>
      <div css={itemStyle}>
        <Avatar size="large" userName={me.name} userEmail={email} source={me.profileImage} />
      </div>
      <Popover.Section>내 채널</Popover.Section>
      <div css={[itemStyle, ChannelLabel]}>
        <Typography type="body">
          {isSchoolAdmin
            ? `${me.name}님이 소유/편집 중이거나 접근 가능한 클래스/티처 채널이에요`
            : `${me.name}님이 소유하고 있거나 편집자로 추가된 채널이에요`}
        </Typography>
      </div>
    </>
  ) : (
    <LoadingSpinner />
  );

  return (
    <>
      <style>
        {`body {
          ${onMobile} {
            overflow: hidden;
          }
    }`}
      </style>
      {/* eslint-disable-next-line */}
      <div
        css={[cssStyle, UserMenuContentWrapper]}
        onClick={stopPropagation}
        onTouchStart={stopPropagation}
      >
        <div css={TitleWrapper}>
          <Popover.Section isTitle>계정 및 설정</Popover.Section>
          <Button
            type="plainWhite"
            icon="times_light"
            size="medium"
            onClick={onClose}
            cssStyle={CloseButton}
          />
        </div>
        <section css={MobileScrollContainer}>
          <Popover.Section>내 계정</Popover.Section>
          {avatarContent}
          <Channels onClick={onClose} />
          <ActionList
            cssStyle={ActionListStyle}
            size="big"
            onActionAnyItem={onClose}
            items={[
              {
                content: '새 채널 추가',
                icon: 'plus_solid',
                onAction: () => {
                  if (channels && channels.length > 4 && !isAdmin && !isSchoolAdmin) {
                    toast({ message: '채널은 5개 까지 생성 가능합니다.', type: 'error' });
                  } else {
                    history.push('/createApp');
                  }
                }
              },
              {
                content: '공지사항',
                icon: 'bullhorn_solid',
                onAction: () => window.open('https://rwd.to/realworldstudio-notice')
              },
              {
                content: '업데이트 소식',
                icon: 'lightbulb_solid',
                onAction: () => window.open('https://rwd.to/realworldstudio-updates')
              },
              {
                content: '문의하기',
                icon: 'comment_dots_solid',
                onAction: () => {
                  if (ChannelIO) {
                    ChannelIO('showMessenger');
                  }
                }
              },
              {
                content: '로그아웃',
                icon: 'sign_out_alt_solid',
                onAction: async () => {
                  await logout();
                  window.location.href = '/';
                }
              }
            ]}
          />
        </section>
      </div>
    </>
  );
}

function stopPropagation(event: React.MouseEvent | React.TouchEvent) {
  event.stopPropagation();
}

const UserMenuContentWrapper = css`
  border-radius: ${spacing.borderRadius.xlarge};
  ${onMobile} {
    border-radius: 0px;
    overflow-y: auto;
  }
`;

const MobileScrollContainer = css`
  ${onMobile} {
    overflow-y: scroll;
    height: calc(100% - 53px);
  }
`;

const CloseButton = css`
  display: none;
  ${onMobile} {
    display: block;
    color: black;
    position: absolute;
    top: ${spacing.margin.medium};
    right: ${spacing.margin.medium};
  }
`;

const TitleWrapper = css`
  border-radius: 16px;
  > div {
    border-radius: ${spacing.borderRadius.xlarge} ${spacing.borderRadius.xlarge} 0 0;
    border-bottom: 0;
  }
`;

const itemStyle = css`
  padding: ${spacing.padding.medium} ${spacing.padding.large};
  background-color: ${color.surface_default_subdued};

  :hover {
    background-color: ${color.surface_default_hovered};
  }
`;

const ItemSelected = css`
  background-color: ${color.surface_primary_default};
`;

const ItemList = css`
  ${itemStyle}

  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;

  box-shadow: inset 0px -1px 0px #ededed;
`;

const ChannelStyle = css`
  flex: 1 0;
  :focus-visible {
    border-radius: ${spacing.borderRadius.small};
  }

  margin-right: ${spacing.margin.large};
`;

const ActionListStyle = css`
  ul,
  ul:hover {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
  }

  .Section-withoutTitle {
    @media (width: ${breakpoint + 1}) {
      width: 368px;
    }
  }
`;

const CHANNEL_SEARCH_THRESHOLD = 5;

function Channels({ onClick }: Pick<DOMAttributes<HTMLAnchorElement>, 'onClick'>) {
  const { appId } = useParamsWithMatchPath();

  const { data: me } = useRequest<MyInfo>('/api/me');
  const { data: { data: apps } = {} } = useRequest<CommonResponseModel<ApplicationResponseModel[]>>(
    '/apps',
    undefined,
    {
      headers: {
        'x-rwd-api-version': '1.1'
      }
    }
  );

  const channels = me && apps ? mapChannelsByRole(apps, me) : undefined;

  const [defaultChannelId, setDefaultChannelId] = useLocalStorage('defaultChannelID');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const defaultChannelIdIsInvalid = !channels?.find(
      (channel) => channel?.id === defaultChannelId
    );

    if (channels && channels[0]?.id && defaultChannelIdIsInvalid) {
      setDefaultChannelId(channels[0].id);
    }
  }, [channels === undefined]);

  if (!channels) return <LoadingSpinner />;

  const targetIndex = channels.findIndex((channel) => channel?.id === defaultChannelId);

  const sortedChannels =
    targetIndex === -1
      ? channels
      : [
          channels[targetIndex],
          ...channels.slice(0, targetIndex),
          ...channels.slice(targetIndex + 1)
        ];

  const showSearch = channels.length > CHANNEL_SEARCH_THRESHOLD;

  const filteredChannels = useMemo(() => {
    if (!searchQuery.trim()) return sortedChannels;
    const query = searchQuery.trim().toLowerCase();
    return sortedChannels.filter((channel) => channel.name?.toLowerCase().includes(query));
  }, [sortedChannels, searchQuery]);

  return (
    <div>
      {showSearch && (
        <div css={SearchInputWrapper}>
          <Input
            type="search"
            placeholder="채널 검색"
            prefix={<Icon icon="search_solid" size="20px" />}
            value={searchQuery}
            onChange={(value) => setSearchQuery(value)}
            clearButton
            onClearButtonClick={() => setSearchQuery('')}
            cssStyle={SearchInputStyle}
          />
        </div>
      )}
      <ul css={ChannelsWrapper} role="menu" aria-label="팝오버" tabIndex={-1}>
      {filteredChannels.map((channel) => {
        const isDefault = defaultChannelId === channel.id;
        const channelBadgeProps = isDefault
          ? ({ type: 'purple' } as const)
          : ({ type: 'gray', isOutline: true } as const);

        const isSelectedChannel = appId === channel.id;

        return (
          <li
            key={channel.id}
            role="menuitem"
            tabIndex={-1}
            css={[ItemList, isSelectedChannel ? ItemSelected : undefined]}
          >
            <Link
              to={`/apps/${channel.id}`}
              onClick={onClick}
              css={[RowFlex, KeyboardFocusStyle, ChannelStyle]}
            >
              <Avatar size="large" isCreator source={channel.imageUrl} />
              <div css={ChannelProfile}>
                <Typography type="body" cssStyle={ChannelName}>
                  {channel.name}
                </Typography>
                {channel.level && <Badge>{levelToKorean[channel.level]}</Badge>}
              </div>
            </Link>
            <Button
              type="plainWhite"
              onClick={() => setDefaultChannelId(channel.id ?? '')}
              cssStyle={BadgeButtonStyle}
            >
              <Badge {...channelBadgeProps}>기본</Badge>
            </Button>
          </li>
        );
      })}
    </ul>
    </div>
  );
}

const SearchInputWrapper = css`
  padding: ${spacing.padding.small} ${spacing.padding.large};
  background-color: ${color.surface_default_subdued};
`;

const SearchInputStyle = css`
  width: 100%;
`;

const AlwaysShowScroll = css`
  &::-webkit-scrollbar {
    -webkit-appearance: none;
  }

  &::-webkit-scrollbar:vertical {
    width: 11px;
  }

  &::-webkit-scrollbar:horizontal {
    height: 11px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 8px;
    border: 2px solid white; /* should match background, can't be transparent */
    background-color: rgba(0, 0, 0, 0.5);
  }
`;

const ChannelsWrapper = css`
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 192px;
  overflow-y: auto;

  ${AlwaysShowScroll}

  ${onMobile} {
    max-height: initial;
    overflow-y: initial;
  }
`;

const ChannelProfile = css`
  margin-left: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ChannelName = css`
  margin-bottom: ${spacing.margin.small};
`;

const ChannelLabel = css`
  > div {
    color: ${color.text_subdued};
  }
  padding: 10px ${spacing.padding.large};
`;

const BadgeButtonStyle = css`
  border: 0;
  padding: 0 !important;
  border-radius: 100px;
`;
